import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { EventRow, JSONValue } from '@/lib/types'

/**
 * Editable-event context — every logger reads this to know if it's editing.
 * Reads `?edit=<uuid>` from the URL; if present, fetches the original event.
 * The logger uses `original.payload` as initial state and passes `original.id` as
 * `supersedes` when calling emitEvent — that creates a new correcting event chained to the original.
 */

export type EditableEvent = {
  loading: boolean
  original: EventRow | null
  error: string | null
  supersedesId: string | null
}

export function useEditableEvent(expectedEventTypes: string[] | null = null): EditableEvent {
  const [params] = useSearchParams()
  const editId = params.get('edit')
  const [loading, setLoading] = useState(!!editId)
  const [original, setOriginal] = useState<EventRow | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!editId) {
      setLoading(false)
      setOriginal(null)
      setError(null)
      return
    }
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const { data, error } = await supabase.from('events').select('*').eq('id', editId).maybeSingle()
        if (cancelled) return
        if (error) throw new Error(error.message)
        if (!data) throw new Error("Couldn't find that entry — it may have been deleted or you don't have access.")
        const row = data as EventRow
        if (expectedEventTypes && !expectedEventTypes.includes(row.event_type)) {
          throw new Error(`Cannot edit here — this entry is a ${row.event_type}, not for this logger.`)
        }
        setOriginal(row)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
    // Only re-fetch when the edit ID changes; expectedEventTypes is a stable array per caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId])

  return { loading, original, error, supersedesId: editId ?? null }
}

/** Route a Timeline event to the correct logger's edit URL. Returns null for non-editable events. */
export function editRouteFor(event: EventRow): string | null {
  switch (event.event_type) {
    case 'early_care.feeding.recorded':
    case 'early_care.feeding.corrected':
      return `/log/feed?edit=${event.id}`
    case 'early_care.diaper.recorded':
    case 'early_care.diaper.corrected':
      return `/log/diaper?edit=${event.id}`
    case 'early_care.sleep.started':
    case 'early_care.sleep.ended':
      return `/log/sleep?edit=${event.id}`
    case 'growth.recorded':
      return `/log/growth?edit=${event.id}`
    case 'early_care.medication.recorded':
    case 'early_care.medication.corrected':
      return `/log/medication?edit=${event.id}`
    case 'note.created':
      return `/log/note?edit=${event.id}`
    default:
      return null
  }
}

/** Map an event type to its `.corrected` variant when a correction is being emitted. */
export function correctionEventType(originalType: string): string {
  switch (originalType) {
    case 'early_care.feeding.recorded':
    case 'early_care.feeding.corrected':
      return 'early_care.feeding.corrected'
    case 'early_care.diaper.recorded':
    case 'early_care.diaper.corrected':
      return 'early_care.diaper.corrected'
    case 'early_care.medication.recorded':
    case 'early_care.medication.corrected':
      return 'early_care.medication.corrected'
    default:
      // For events without a .corrected variant, reuse original type — projections still see
      // the superseded relationship via supersedes_event_id.
      return originalType
  }
}

export function payloadValue<T = JSONValue>(payload: JSONValue | null, key: string): T | undefined {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return undefined
  return (payload as Record<string, unknown>)[key] as T | undefined
}
